// lib/sfx-catalogue.ts
import type { SoundEffect } from '#types/types';

export interface SfxPlace {
  /** Where in the app, in words: the module and what the user (or the agent) just did. */
  where: string;
  /** The file that plays it; lib/sfx-catalogue.test.ts holds this list to the code. */
  file: string;
}

// EVERY UI SOUND AND WHERE IT PLAYS (owner, 2026-09-29: 'somewhere where all SFX are listed with
// where they are implemented, so I can know'). Read by Settings > Admin > Sounds. The test fails
// when a playUISound call appears in a file this list does not name, or a listed place stops
// playing it, so the page cannot drift from the code.
export const SFX_CATALOGUE: Record<SoundEffect, { label: string; places: SfxPlace[] }> = {
  GENERATE_START: { label: 'Generation starts', places: [
    { where: 'Studio: Generate (Enter or the Generate button)', file: 'kernel/Kernel.tsx' },
    { where: 'Studio: Quick enhance and Quick upscale', file: 'kernel/useModels.ts' },
    { where: 'Studio: the agent launches a generation (spectacle mode)', file: 'kernel/useCommands.ts' },
  ] },
  SUCCESS: { label: 'Success', places: [
    { where: 'Studio: a generation lands on the canvas', file: 'kernel/useGenerationTasks.ts' },
    { where: "Studio: a layer's colours are harmonised", file: 'kernel/useLayers.ts' },
    { where: 'Source: a scoring campaign finishes', file: 'kernel/useScoringCampaigns.ts' },
    { where: "Source: a contact's phone number is found", file: 'kernel/useLeasingWorkflows.ts' },
  ] },
  UNDO: { label: 'Undo', places: [{ where: 'Everywhere: Undo (Ctrl/⌘ Z)', file: 'kernel/Kernel.tsx' }] },
  REDO: { label: 'Redo', places: [{ where: 'Everywhere: Redo', file: 'kernel/Kernel.tsx' }] },
  RESIZE: { label: 'Frame or resize', places: [
    { where: 'Studio: the agent frames the view or moves the generation area', file: 'components/designCommands.ts' },
  ] },
  VALUE_TICK: { label: 'Setting changed', places: [
    { where: 'Studio: the agent changes a model setting, the selected layers or the aspect ratio', file: 'components/designCommands.ts' },
  ] },
  TYPE_KEY: { label: 'Typing', places: [
    { where: 'Studio: the agent types a prompt into a model setting', file: 'components/designCommands.ts' },
    { where: 'Daimon: she types into a field or an assistant box', file: 'components/daimon/DaimonVoice.tsx' },
  ] },
  DAIMON_CLICK: { label: 'Daimon clicks', places: [
    { where: 'Daimon: she presses a button, a tab, a row or a point on the screen', file: 'components/daimon/DaimonVoice.tsx' },
  ] },
  TAB_SWITCH: { label: 'Workspace switch', places: [
    { where: 'Studio: the agent switches workspace', file: 'components/designCommands.ts' },
  ] },
  QUICK_SWITCH: { label: 'Quick switch', places: [{ where: 'Crop tool: the image appears (spectacle mode)', file: 'components/ImageCropModal.tsx' }] },
  CUT: { label: 'Cut', places: [{ where: 'Crop tool: a crop is confirmed', file: 'components/ImageCropModal.tsx' }] },
  HEATMAP: { label: 'Heatmap on', places: [{ where: 'Studio: the resolution heatmap is turned on', file: 'components/BottomBar.tsx' }] },
  CONFIRM: { label: 'Dictation captured', places: [{ where: 'Any chat: voice dictation is captured', file: 'components/VoiceInputButton.tsx' }] },
  VOICE_START: { label: 'Voice starts', places: [{ where: 'Daimon: a voice conversation connects', file: 'components/daimon/DaimonVoice.tsx' }] },
  VOICE_END: { label: 'Voice ends', places: [{ where: 'Daimon: a voice conversation ends', file: 'components/daimon/DaimonVoice.tsx' }] },
  WEB_SEARCH: { label: 'Web search', places: [{ where: 'Daimon: loops while it searches the web', file: 'components/daimon/DaimonVoice.tsx' }] },
  INTENSE_NOTIFICATION: { label: 'Notification', places: [] },
};
